import { useEffect, useRef, useState } from 'react';
import { download } from '../api.ts';
import { pagesFailed } from '../screens/project/plates.ts';

/**
 * A PDF on its page in Files, shown as its pages one after another down the page (L-35; Jason, 2026-10-30: "opening a pdf
 * file needs to show the pdf in browser right here, all pages, no viewer or secondary scroll bar, just one page after
 * another, full width where this white box is one below the other"): each page a picture drawn by pdf.js as wide as the
 * box, in order, the page laid out to every page's shape before any is drawn so nothing jumps. pdf.js is the one the
 * design reviews split drawing sets with (`pdfSet.ts`), loaded only here and only then. A PDF it cannot read says why
 * (`pagesFailed`); the file as it came stays a click away under it.
 */
export function PdfPages({ url, name }: { url: string; name: string }) {
  const box = useRef<HTMLDivElement | null>(null);
  const [sizes, setSizes] = useState<{ width: number; height: number }[] | null>(null);
  const [drawn, setDrawn] = useState<string[]>([]);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let gone = false;
    const made: string[] = [];
    let close: (() => Promise<void>) | null = null;
    (async () => {
      try {
        // Through api.ts as every request is, a GET that changes nothing and says nothing (as Reviews reads a PDF).
        const bytes = await (await download(url.replace(/^\/api/, ''))).arrayBuffer();
        const { openPages } = await import('../screens/project/pdfSet.ts');
        const pdf = await openPages(bytes);
        close = pdf.close;
        if (gone) return;
        setSizes(pdf.sizes);
        const width = box.current?.clientWidth || 960;
        // One after another, each shown as it is drawn.
        for (let i = 0; i < pdf.sizes.length && !gone; i++) {
          const picture = URL.createObjectURL(await pdf.draw(i, width, window.devicePixelRatio || 1));
          made.push(picture);
          if (!gone) setDrawn([...made]);
        }
      } catch (e) {
        if (!gone) setFailed(pagesFailed(name, e));
      }
    })();
    return () => {
      gone = true;
      for (const picture of made) URL.revokeObjectURL(picture);
      void close?.();
    };
  }, [url, name]);

  if (failed) {
    return (
      <div className="rounded-[2px] border border-rule bg-sheet p-6">
        <p className="font-display text-[1.25rem]">{name}</p>
        <p className="mt-1 text-sm text-ink-2">{failed}</p>
      </div>
    );
  }
  return (
    <div ref={box} role="group" aria-label={`The pages of ${name}`} className="grid gap-4">
      {sizes ? null : <p className="meta">Drawing its pages…</p>}
      {sizes?.map((size, i) =>
        drawn[i] ? (
          <img key={i} src={drawn[i]} alt={`Page ${i + 1} of ${sizes.length}`} className="block h-auto w-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.12)]" style={{ aspectRatio: `${size.width} / ${size.height}` }} />
        ) : (
          <div key={i} className="w-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.12)]" style={{ aspectRatio: `${size.width} / ${size.height}` }} aria-label={`Page ${i + 1} of ${sizes.length}, being drawn`} />
        ),
      )}
    </div>
  );
}
